import { OPERATIONAL_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { createAndFireEvent, type CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { type SlackServiceAnalytics } from './AbstractSlackService';
import { ANALYTICS_CHANNEL } from './constants';
import { type SlackChannel, type SlackUser } from './share-to-slack/common/types';
import { type AtlassianSlackProduct, type ConfluenceSlackServiceAnalytics } from './types';

export type OperationalError = {
	errorMessage?: string;
	status: number;
};

const createAndFireEventOnPeopleTeams = createAndFireEvent(ANALYTICS_CHANNEL);

enum AnalyticsActionSubject {
	INTEGRATION_SUCCEEDED = 'integrationEnabled',
	INTEGRATION_TEAMS = 'integrationTeams',
	INTEGRATION_CONVERSATIONS = 'integrationConversations',
	INTEGRATION_METADATA = 'integrationMetadata',
	INTEGRATION_TOKEN = 'integrationCsrfToken',
	SHARE = 'share',
}

enum AnalyticsAction {
	FAILED = 'failed',
	SUCCEEDED = 'succeeded',
}

export const triggerAnalyticsForSlackEnabledSucceeded = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
) => {
	const fireEventWithPayload = createAndFireEventOnPeopleTeams({
		eventType: OPERATIONAL_EVENT_TYPE,
		actionSubject: AnalyticsActionSubject.INTEGRATION_SUCCEEDED,
		action: AnalyticsAction.SUCCEEDED,
	});
	fireEventWithPayload(createAnalyticsEvent);
};

export const triggerAnalyticsForSlackEnabledFailed = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ status, errorMessage }: OperationalError,
) => {
	const fireEventWithPayload = createAndFireEventOnPeopleTeams({
		eventType: OPERATIONAL_EVENT_TYPE,
		actionSubject: AnalyticsActionSubject.INTEGRATION_SUCCEEDED,
		action: AnalyticsAction.FAILED,
		attributes: {
			status,
			errorMessage,
		},
	});
	fireEventWithPayload(createAnalyticsEvent);
};

export const triggerAnalyticsForFetchTeamsSucceeded = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ teams, pages }: { pages: number; teams: number },
) => {
	const fireEventWithPayload = createAndFireEventOnPeopleTeams({
		eventType: OPERATIONAL_EVENT_TYPE,
		actionSubject: AnalyticsActionSubject.INTEGRATION_TEAMS,
		action: AnalyticsAction.SUCCEEDED,
		attributes: { teams, pages },
	});
	fireEventWithPayload(createAnalyticsEvent);
};

export const triggerAnalyticsForFetchTeamsFailed = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ page, status, errorMessage }: OperationalError & { page: number },
) => {
	const fireEventWithPayload = createAndFireEventOnPeopleTeams({
		eventType: OPERATIONAL_EVENT_TYPE,
		actionSubject: AnalyticsActionSubject.INTEGRATION_TEAMS,
		action: AnalyticsAction.FAILED,
		attributes: { page, status, errorMessage },
	});
	fireEventWithPayload(createAnalyticsEvent);
};

export const triggerAnalyticsForFetchUsersSucceeded = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{
		slackTeamId,
		conversationTypes,
		conversations,
		pages,
	}: {
		conversations: number;
		conversationTypes: ['public', 'private'] | ['directMessage'];
		pages: number;
		slackTeamId: string;
	},
) => {
	const fireEventWithPayload = createAndFireEventOnPeopleTeams({
		eventType: OPERATIONAL_EVENT_TYPE,
		actionSubject: AnalyticsActionSubject.INTEGRATION_CONVERSATIONS,
		action: AnalyticsAction.SUCCEEDED,
		attributes: {
			integrationTeamId: slackTeamId,
			conversationTypes,
			conversations,
			pages,
		},
	});
	fireEventWithPayload(createAnalyticsEvent);
};

export const triggerAnalyticsForFetchUsersFailed = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{
		slackTeamId,
		conversationTypes,
		page,
		status,
		errorMessage,
	}: OperationalError & {
		conversationTypes: ['public', 'private'] | ['directMessage'];
		page: number;
		slackTeamId: string;
	},
) => {
	const fireEventWithPayload = createAndFireEventOnPeopleTeams({
		eventType: OPERATIONAL_EVENT_TYPE,
		actionSubject: AnalyticsActionSubject.INTEGRATION_CONVERSATIONS,
		action: AnalyticsAction.FAILED,
		attributes: {
			integrationTeamId: slackTeamId,
			conversationTypes,
			page,
			status,
			errorMessage,
		},
	});
	fireEventWithPayload(createAnalyticsEvent);
};

export const triggerAnalyticsForFetchConnectMetadataSucceeded = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
) => {
	const fireEventWithPayload = createAndFireEventOnPeopleTeams({
		eventType: OPERATIONAL_EVENT_TYPE,
		actionSubject: AnalyticsActionSubject.INTEGRATION_METADATA,
		action: AnalyticsAction.SUCCEEDED,
	});
	fireEventWithPayload(createAnalyticsEvent);
};

export const triggerAnalyticsForFetchConnectMetadataFailed = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	params: OperationalError,
) => {
	const fireEventWithPayload = createAndFireEventOnPeopleTeams({
		eventType: OPERATIONAL_EVENT_TYPE,
		actionSubject: AnalyticsActionSubject.INTEGRATION_METADATA,
		action: AnalyticsAction.FAILED,
		attributes: {
			...params,
		},
	});
	fireEventWithPayload(createAnalyticsEvent);
};

export const triggerAnalyticsForCsrfSucceeded = (createAnalyticsEvent: CreateUIAnalyticsEvent) => {
	const fireEventWithPayload = createAndFireEventOnPeopleTeams({
		eventType: OPERATIONAL_EVENT_TYPE,
		actionSubject: AnalyticsActionSubject.INTEGRATION_METADATA,
		action: AnalyticsAction.SUCCEEDED,
	});
	fireEventWithPayload(createAnalyticsEvent);
};

export const triggerAnalyticsForCsrfFailed = (createAnalyticsEvent: CreateUIAnalyticsEvent) => {
	const fireEventWithPayload = createAndFireEventOnPeopleTeams({
		eventType: OPERATIONAL_EVENT_TYPE,
		actionSubject: AnalyticsActionSubject.INTEGRATION_TOKEN,
		action: AnalyticsAction.FAILED,
	});
	fireEventWithPayload(createAnalyticsEvent);
};

const productActionSubjectId: {
	[key in AtlassianSlackProduct]: string;
} = {
	jira: 'issue',
	confluence: 'page',
	// default to issue at the moment but should
	// be something else depending on what is actually shared
	// once we implement sharing with the platform slack app
	platform: 'issue',
};
type PagedOperationalError = OperationalError & {
	page: number;
};
type ShareToSlackOperationalEventType = {
	conversationId: string;
	conversationType: SlackChannel['type'] | SlackUser['type'];
	messageLength?: number;
	product: AtlassianSlackProduct;
	slackTeamId: string;
	userCount?: number;
};
type ShareToSlackFailedOperationalEventType = ShareToSlackOperationalEventType & OperationalError;
export const triggerShareSucceeded = (
	{
		product,
		slackTeamId,
		conversationType,
		conversationId,
		messageLength,
		userCount,
	}: ShareToSlackOperationalEventType,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
) => {
	const fireEventWithPayload = createAndFireEventOnPeopleTeams({
		eventType: OPERATIONAL_EVENT_TYPE,
		actionSubject: AnalyticsActionSubject.SHARE,
		action: AnalyticsAction.SUCCEEDED,
		actionSubjectId: productActionSubjectId[product],
		attributes: {
			integrationTeamId: slackTeamId,
			integrationConversationType: conversationType,
			integrationConversationId: conversationId,
			messageLength,
			workspaceSize: userCount,
		},
		source: 'integrationShareDialog',
	});
	fireEventWithPayload(createAnalyticsEvent);
};
export const triggerShareFailed = (
	{
		product,
		slackTeamId,
		conversationType,
		conversationId,
		status,
		errorMessage,
	}: ShareToSlackFailedOperationalEventType,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
) => {
	const fireEventWithPayload = createAndFireEventOnPeopleTeams({
		eventType: OPERATIONAL_EVENT_TYPE,
		actionSubject: AnalyticsActionSubject.SHARE,
		action: AnalyticsAction.FAILED,
		actionSubjectId: productActionSubjectId[product],
		attributes: {
			integrationTeamId: slackTeamId,
			integrationConversationType: conversationType,
			integrationConversationId: conversationId,
			status,
			errorMessage,
		},
		source: 'integrationShareDialog',
	});
	fireEventWithPayload(createAnalyticsEvent);
};
export const triggerConversationsSucceeded = (
	{
		slackTeamId,
		conversationTypes,
		conversations,
		pages,
		actionSubjectId,
	}: {
		actionSubjectId: string;
		conversations: number;
		conversationTypes: ['public', 'private'] | ['directMessage'];
		pages: number;
		slackTeamId: string;
	},
	createAnalyticsEvent: CreateUIAnalyticsEvent,
) => {
	const fireEventWithPayload = createAndFireEventOnPeopleTeams({
		eventType: OPERATIONAL_EVENT_TYPE,
		actionSubject: 'integrationConversations',
		action: AnalyticsAction.SUCCEEDED,
		actionSubjectId,
		attributes: {
			integrationTeamId: slackTeamId,
			conversationTypes,
			conversations,
			pages,
		},
		source: 'integrationShareDialog',
	});
	fireEventWithPayload(createAnalyticsEvent);
};
export const triggerConversationsFailed = (
	{
		slackTeamId,
		conversationTypes,
		page,
		status,
		errorMessage,
		actionSubjectId,
	}: PagedOperationalError & {
		actionSubjectId: string;
		conversationTypes: ['public', 'private'] | ['directMessage'];
		slackTeamId: string;
	},
	createAnalyticsEvent: CreateUIAnalyticsEvent,
) => {
	const fireEventWithPayload = createAndFireEventOnPeopleTeams({
		eventType: OPERATIONAL_EVENT_TYPE,
		actionSubject: 'integrationConversations',
		action: AnalyticsAction.FAILED,
		actionSubjectId,
		attributes: {
			integrationTeamId: slackTeamId,
			conversationTypes,
			page,
			status,
			errorMessage,
		},
		source: 'integrationShareDialog',
	});
	fireEventWithPayload(createAnalyticsEvent);
};

export const getSlackServiceAnalytics = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
): SlackServiceAnalytics => ({
	onGetEnabledSucceeded: () => triggerAnalyticsForSlackEnabledSucceeded(createAnalyticsEvent),
	onGetEnabledFailed: (params) =>
		triggerAnalyticsForSlackEnabledFailed(createAnalyticsEvent, params),
	onFetchTeamsSucceeded: (params) =>
		triggerAnalyticsForFetchTeamsSucceeded(createAnalyticsEvent, params),
	onFetchTeamsFailed: (params) => triggerAnalyticsForFetchTeamsFailed(createAnalyticsEvent, params),
	onFetchUsersSucceeded: ({ teamId, users, pages }) =>
		triggerAnalyticsForFetchUsersSucceeded(createAnalyticsEvent, {
			slackTeamId: teamId || '',
			conversationTypes: ['directMessage'],
			conversations: users || 0,
			pages,
		}),
	onFetchUsersFailed: ({ teamId, page, status, errorMessage }) =>
		triggerAnalyticsForFetchUsersFailed(createAnalyticsEvent, {
			slackTeamId: teamId,
			conversationTypes: ['directMessage'],
			page,
			status,
			errorMessage,
		}),
	onShareSucceeded: ({
		product,
		teamId,
		conversationType,
		conversationId,
		messageLength,
		userCount,
	}) => {
		triggerShareSucceeded(
			{
				product,
				slackTeamId: teamId,
				conversationType,
				conversationId,
				messageLength,
				userCount,
			},
			createAnalyticsEvent,
		);
	},
	onShareFailed: ({ product, teamId, conversationType, conversationId, status, errorMessage }) => {
		triggerShareFailed(
			{
				product,
				slackTeamId: teamId,
				conversationType,
				conversationId,
				status,
				errorMessage,
			},
			createAnalyticsEvent,
		);
	},
	onFetchChannelsSucceeded: ({ teamId, channels, pages }) => {
		triggerConversationsSucceeded(
			{
				slackTeamId: teamId,
				conversationTypes: ['public', 'private'],
				conversations: channels,
				pages,
				actionSubjectId: 'channels',
			},
			createAnalyticsEvent,
		);
	},
	onFetchChannelsFailed: ({ teamId, page, status, errorMessage }) => {
		triggerConversationsFailed(
			{
				slackTeamId: teamId,
				conversationTypes: ['public', 'private'],
				page,
				status,
				errorMessage,
				actionSubjectId: 'channels',
			},
			createAnalyticsEvent,
		);
	},
});

export const getConfluenceSlackServiceAnalytics = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
): ConfluenceSlackServiceAnalytics => ({
	onFetchConnectMetadataSucceeded: () =>
		triggerAnalyticsForFetchConnectMetadataSucceeded(createAnalyticsEvent),
	onFetchConnectMetadataFailed: (params) =>
		triggerAnalyticsForFetchConnectMetadataFailed(createAnalyticsEvent, params),
	onRefreshIntegrationCsrfTokenSucceeded: () =>
		triggerAnalyticsForCsrfSucceeded(createAnalyticsEvent),
	onRefreshIntegrationCsrfTokenFailed: () => triggerAnalyticsForCsrfFailed(createAnalyticsEvent),
});
